import React from 'react';
import { Loader2 } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';

// Form Details Modal Component (reusable)
const FormDetailsModal = ({ selectedForm, auditFormData, isLoadingFormData, formDataError }) => {
  if (isLoadingFormData) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        <span>Loading form details...</span>
      </div>
    );
  }

  if (formDataError) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 mb-2">Error loading form details</div>
        <div className="text-sm text-gray-500">{formDataError}</div>
      </div>
    );
  }

  if (!auditFormData?.data) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-500">No detailed form data available</div>
      </div>
    );
  }

  const formDetails = auditFormData.data;

  return (
    <ScrollArea className="max-h-[60vh] pr-4">
      <div className="space-y-4">
        {/* Basic Form Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-500">Form Name</label>
            <p className="text-lg font-semibold">{formDetails.form_name || 'N/A'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Form Code</label>
            <p className="text-lg font-semibold">{formDetails.form_code || 'N/A'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Italian Name</label>
            <p className="text-lg font-semibold">{formDetails.form_name_italian || 'N/A'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Total Fields</label>
            <p className="text-lg font-semibold">{formDetails.total_fields_count || 0}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Total Points</label>
            <p className="text-lg font-semibold">{formDetails.total_possible_points || 0}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Passing Threshold</label>
            <p className="text-lg font-semibold">{formDetails.passing_threshold || 0}%</p>
          </div>
        </div>

        {/* Production Line Information */}
        {formDetails.production_line && (
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-2">Production Line</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Line Name</label>
                <p className="font-medium">{formDetails.production_line.line_name || formDetails.production_line || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Line ID</label>
                <p className="font-medium">{formDetails.production_line.line_id || 'N/A'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Product Information */}
        {formDetails.product && (
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-2">Product</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Product Name</label>
                <p className="font-medium">{formDetails.product.product_name || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Model Number</label>
                <p className="font-medium">{formDetails.product.model_number || 'N/A'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Categories */}
        {formDetails.form_structure && formDetails.form_structure.length > 0 && (
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-3">Categories ({formDetails.form_structure.length})</h3>
            <div className="space-y-2">
              {formDetails.form_structure.map((category, index) => (
                <div key={category.id || index} className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{category.title}</p>
                      {category.categoryData?.category_code_it && (
                        <p className="text-sm text-gray-600">{category.categoryData.category_code_it}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="ml-2">
                        {category.fields?.length || 0} Items
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status and Dates */}
        <div className="border-t pt-4">
          <h3 className="text-lg font-semibold mb-2">Status & Dates</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Status</label>
              <Badge variant={formDetails.is_published ? "default" : "secondary"}>
                {formDetails.is_published ? "Published" : "Draft"}
              </Badge>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Created</label>
              <p className="font-medium">
                {formDetails.created_at ? new Date(formDetails.created_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </ScrollArea>
  );
};

export default FormDetailsModal;